import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Facebook, Instagram, Linkedin } from "lucide-react";
import GoogleIcon from "@/components/GoogleIcon";
import { AvailabilityBar } from "@/components/AvailabilityBar";

const NAV = [
  { to: "/", label: "Start" },
  { to: "/leistungen", label: "Leistungen" },
  { to: "/privatkoch", label: "Privatkoch" },
  { to: "/vermittlung", label: "Vermittlung" },
  { to: "/preise", label: "Preise" },
  { to: "/gutscheine", label: "Gutscheine" },
  { to: "/ueber-mich", label: "Über mich" },
  { to: "/kontakt", label: "Kontakt" },
];

function Header() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [loc.pathname]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 backdrop-blur-xl bg-[#FAFAF7]/80 border-b border-[#E3DECE]"
    >
      <AvailabilityBar />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 h-32 md:h-40 flex items-center justify-center md:justify-between">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="flex items-center gap-3"
          aria-label="Lukas Stutz — Startseite"
        >
          <img
            src="/logo.png"
            alt="Lukas Stutz"
            className="h-24 md:h-32 w-auto object-contain"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `text-sm tracking-wider uppercase transition-colors ${
                  isActive
                    ? "text-[#BA6D52]"
                    : "text-[#2C2A26] hover:text-[#BA6D52]"
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
          <Link
            to="/anfrage"
            data-testid="header-cta-anfrage"
            className="bg-[#BA6D52] text-white hover:bg-[#9E5841] px-6 py-3 text-xs uppercase tracking-widest transition-colors"
          >
            Anfrage
          </Link>
        </nav>

        <button
          aria-label="Menü"
          data-testid="mobile-menu-toggle"
          className="md:hidden absolute right-6 top-1/2 -translate-y-1/2 text-[#2C2A26]"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-[#E3DECE] bg-[#FAFAF7]"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-nav-${n.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `text-sm tracking-wider uppercase ${
                    isActive ? "text-[#BA6D52]" : "text-[#2C2A26]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/anfrage"
              data-testid="mobile-cta-anfrage"
              className="bg-[#BA6D52] text-white px-6 py-3 text-xs uppercase tracking-widest text-center"
            >
              Anfrage stellen
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#2C2A26] text-[#FAFAF7] mt-24"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid grid-cols-1 md:grid-cols-3 gap-12">
        <div>
          <p className="font-serif text-3xl font-light">Lukas Stutz</p>
          <p className="mt-3 text-sm text-[#FAFAF7]/70 tracking-wide">
            Mietkoch für Gastronomie & Events
          </p>
          <div className="mt-6 flex gap-3">
            <a
              href="https://www.facebook.com/profile.php?id=61590732241072"
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook"
              data-testid="social-facebook"
              className="w-10 h-10 inline-flex items-center justify-center border border-[#FAFAF7]/20 hover:border-[#BA6D52] hover:text-[#BA6D52] transition-colors"
            >
              <Facebook size={16} strokeWidth={1.5} />
            </a>
            <a
              href="https://www.instagram.com/stutz_cuisine/"
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              data-testid="social-instagram"
              className="w-10 h-10 inline-flex items-center justify-center border border-[#FAFAF7]/20 hover:border-[#BA6D52] hover:text-[#BA6D52] transition-colors"
            >
              <Instagram size={16} strokeWidth={1.5} />
            </a>
            <a
              href="https://www.linkedin.com/in/lukas-stutz-680947414/"
              target="_blank"
              rel="noreferrer"
              aria-label="LinkedIn"
              data-testid="social-linkedin"
              className="w-10 h-10 inline-flex items-center justify-center border border-[#FAFAF7]/20 hover:border-[#BA6D52] hover:text-[#BA6D52] transition-colors"
            >
              <Linkedin size={16} strokeWidth={1.5} />
            </a>
            <a
              href="https://maps.app.goo.gl/45ATqhF1YTQXjhRX8"
              target="_blank"
              rel="noreferrer"
              aria-label="Google Business Profile"
              data-testid="social-google"
              className="w-10 h-10 inline-flex items-center justify-center border border-[#FAFAF7]/20 hover:border-[#BA6D52] hover:text-[#BA6D52] transition-colors"
            >
              <GoogleIcon size={16} strokeWidth={1.5} />
            </a>
          </div>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] mb-4">
            Navigation
          </p>
          <ul className="space-y-2 text-sm">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link
                  to={n.to}
                  data-testid={`footer-link-${n.to.replace("/", "") || "home"}`}
                  className="text-[#FAFAF7]/80 hover:text-[#BA6D52] transition-colors"
                >
                  {n.label}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/anfrage"
                data-testid="footer-link-anfrage"
                className="text-[#FAFAF7]/80 hover:text-[#BA6D52] transition-colors"
              >
                Anfrage
              </Link>
            </li>
            <li>
              <Link
                to="/mietkoch-werden"
                data-testid="footer-link-mietkoch-werden"
                className="text-[#FAFAF7]/80 hover:text-[#BA6D52] transition-colors"
              >
                Für Köche: ins Netzwerk
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] mb-4">
            Rechtliches
          </p>
          <ul className="space-y-2 text-sm">
            <li>
              <Link
                to="/impressum"
                data-testid="footer-link-impressum"
                className="text-[#FAFAF7]/80 hover:text-[#BA6D52] transition-colors"
              >
                Impressum
              </Link>
            </li>
            <li>
              <Link
                to="/datenschutz"
                data-testid="footer-link-datenschutz"
                className="text-[#FAFAF7]/80 hover:text-[#BA6D52] transition-colors"
              >
                Datenschutz
              </Link>
            </li>
            <li>
              <Link
                to="/agb"
                data-testid="footer-link-agb"
                className="text-[#FAFAF7]/80 hover:text-[#BA6D52] transition-colors"
              >
                AGB
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[#FAFAF7]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row justify-between text-xs text-[#FAFAF7]/50">
          <span>© {new Date().getFullYear()} Lukas Stutz</span>
          <span>Mietkoch · Gastronomie · Events</span>
        </div>
      </div>
    </footer>
  );
}

// Seiten für Gäste/Kunden ohne Website-Navigation (eigene, in sich geschlossene Ansicht)
const BARE_ROUTES = ["/abend/", "/rechnung/", "/rechnung-fuer/"];

export default function Layout({ children }) {
  const loc = useLocation();
  const bare = BARE_ROUTES.some((p) => loc.pathname.startsWith(p));

  if (bare) return <div className="min-h-screen">{children}</div>;

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
